import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, ChevronLeft } from "lucide-react";
import { useChatClient, type ChatClient } from "./useChatClient";
import { EmailChatBody } from "./EmailChatBody";
import { LiveChatBody } from "./LiveChatBody";
import { chatWidgetCopy } from "@/lib/chat-widget-copy";
import { cn } from "@/lib/utils";

export function ChatWidget() {
  const { state, client } = useChatClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [intentPending, setIntentPending] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (intentPending && client) {
      setIntentPending(false);
      client.setPanelOpen(true);
      client.open();
    }
  }, [intentPending, client]);

  useEffect(() => {
    if (client && panelRef.current) {
      client.setCaptchaAnchor(panelRef.current);
    }
  }, [client, open]);

  useEffect(() => {
    if (!threadRef.current) return;
    const el = threadRef.current;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (atBottom) el.scrollTop = el.scrollHeight;
  }, [state?.messages?.length]);

  const onLauncherClick = () => {
    if (!client) {
      setIntentPending(true);
      setOpen(true);
      return;
    }
    setOpen((o) => {
      const next = !o;
      client.setPanelOpen(next);
      if (next) client.open();
      return next;
    });
  };

  const onClose = () => {
    setOpen(false);
    client?.setPanelOpen(false);
    client?.close();
  };

  const onSend = async () => {
    if (!client || !state) return;
    const uploading = (state.attachments ?? []).some(
      (a: any) => a.status === "uploading",
    );
    const ready = (state.attachments ?? [])
      .filter((a: any) => a.status === "uploaded")
      .map((a: any) => a.url);
    if (state.canSend && !uploading && (text.trim() || ready.length)) {
      const msg = text;
      setText("");
      await client.sendMessage({ text: msg, attachments: ready });
    }
  };

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client || !state?.config) return;
    const fields = state.config.contactForm?.fields ?? [];
    const missing = fields
      .filter((f: any) => f.required && !formValues[f.value]?.trim())
      .map((f: any) => f.label);
    if (missing.length) {
      setFormError(`Please fill in: ${missing.join(", ")}`);
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(
        fields.map((f: any) => [f.value, formValues[f.value] ?? ""]),
      );
      await client.submitContactForm({ ...payload, consentGiven: true });
    } catch {
      setFormError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const config = state?.config;
  const error = state?.error;
  const activeChatType = state?.activeChatType;
  const awaitingSelection = state?.awaitingChatTypeSelection;

  return (
    <>
      <button
        type="button"
        onClick={onLauncherClick}
        aria-label={chatWidgetCopy.launcherAriaLabel}
        className="fixed bottom-6 right-6 z-[90] flex h-14 w-14 items-center justify-center rounded-full bg-[#D96B27] text-white shadow-xl transition-transform hover:scale-105 md:bottom-8 md:right-8"
      >
        {open ? (
          <X className="h-6 w-6" />
        ) : (
          <>
            <MessageCircle className="h-6 w-6" />
            {state?.unreadCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
                {state.unreadCount}
              </span>
            )}
          </>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          className={cn(
            "fixed bottom-24 right-4 z-[95] flex h-[min(640px,calc(100dvh-7rem))] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl md:bottom-28 md:right-8",
          )}
        >
          <div className="flex items-center justify-between border-b border-border bg-[#252A2B] px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              {activeChatType && !awaitingSelection && (
                <button
                  type="button"
                  onClick={() => client?.setActiveChatType(null)}
                  aria-label={chatWidgetCopy.backAriaLabel}
                  className="rounded-full p-1 hover:bg-white/10"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              )}
              <div>
                <p className="font-display text-sm font-bold uppercase tracking-wide">
                  {chatWidgetCopy.heading}
                </p>
                <p className="text-[11px] text-white/70">
                  {chatWidgetCopy.subheading}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={chatWidgetCopy.closeAriaLabel}
              className="rounded-full p-1.5 hover:bg-white/10"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            {!config && !error && (
              <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
                Loading chat…
              </div>
            )}

            {error && !config && (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
                <p className="text-sm text-destructive">{error.message}</p>
                <button
                  type="button"
                  onClick={() => client?.open()}
                  className="rounded-full bg-[#D96B27] px-4 py-2 text-sm font-semibold text-white"
                >
                  Retry
                </button>
              </div>
            )}

            {config && awaitingSelection && (
              <div className="flex flex-1 flex-col gap-3 p-6">
                <p className="text-sm font-semibold">
                  How would you like to chat?
                </p>
                <button
                  type="button"
                  onClick={() => client?.setActiveChatType("emailChat")}
                  className="rounded-lg border border-border bg-background px-4 py-3 text-left text-sm font-medium hover:bg-muted"
                >
                  SMS / Email
                </button>
                <button
                  type="button"
                  onClick={() => client?.setActiveChatType("liveChat")}
                  className="rounded-lg border border-border bg-background px-4 py-3 text-left text-sm font-medium hover:bg-muted"
                >
                  Live Chat
                </button>
              </div>
            )}

            {config && activeChatType === "emailChat" && (
              <EmailChatBody
                showContactForm={state?.showContactForm}
                contactFormSubmitted={state?.contactFormSubmitted}
                config={config}
                formValues={formValues}
                setFormValues={setFormValues}
                submitForm={submitForm}
                submitting={submitting}
                formError={formError}
                detectedCountry={state?.detectedCountry}
                invalidPhoneField={state?.error?.field}
              />
            )}

            {config && activeChatType === "liveChat" && (
              <LiveChatBody
                messages={state?.messages ?? []}
                threadRef={threadRef}
                agentTyping={state?.agentTyping}
                hasMoreHistory={state?.hasMoreHistory}
                historyLoading={state?.historyLoading}
                onLoadOlder={() => client?.loadOlderMessages()}
                showContactForm={state?.showContactForm}
                config={config}
                formValues={formValues}
                setFormValues={setFormValues}
                submitForm={submitForm}
                submitting={submitting}
                formError={formError}
                detectedCountry={state?.detectedCountry}
                invalidPhoneField={state?.error?.field}
                feedbackRequested={state?.feedbackRequested}
                feedbackSubmitted={state?.feedbackSubmitted}
                onFeedback={(f) => client?.sendFeedback(f)}
                onRestart={() => client?.restartConversation()}
                ended={state?.ended}
                endedReason={state?.endedReason}
                text={text}
                setText={setText}
                onSend={onSend}
                canSend={state?.canSend}
                captchaRequired={state?.captchaRequired}
                attachments={state?.attachments ?? []}
                attachmentsConfig={config?.liveChat?.attachments}
                onAttach={(f) => client?.uploadAttachment(f)}
                onRemoveAttachment={(id) => client?.removeAttachment(id)}
                connection={state?.connection}
                rateLimited={state?.rateLimited}
                errorMessage={error?.message}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}
